import { LOCATION_TYPE } from './location.actions'
import { CITY_LEVEL_ZOOM, DEFAULT_MAX_MAP_ZOOM } from './location.constants'
import type { AAALocation } from './location.types'
import { isAddressComplete } from './location.utils'
import type { MapLanding, ReferenceProbeOutcome, ReferenceStrategyId } from './map-landing.types'
import { classifyPlaceTypeZoom } from './map-reference.utils'

/** Stable identity for a resolved location; the same format everywhere a landing is keyed. */
export const toLocationKey = (coords: { latitude: number | string; longitude: number | string }): string =>
  `${coords.latitude},${coords.longitude}`

/** The fields of a resolved location a landing is built from — a store BreakdownLocation qualifies too. */
export type LandingSource = Pick<
  AAALocation,
  | 'latitude'
  | 'longitude'
  | 'locationType'
  | 'streetNumber'
  | 'streetName'
  | 'city'
  | 'state'
  | 'placeType'
>

/**
 * DRRWEB-3662 — whether a searched address puts the app into adjust-location mode. The same
 * predicate as the `setBreakdownLocation` effect that dispatches `setShowAdjustLocation`
 * (location.effects.ts): a complete address with a house number is confirmed as is; anything else
 * (a street, an intersection, a locality, a postal code…) is adjusted at the city zoom — except on
 * the `geocode` arm, where the place-type table decides the adjust-mode zoom instead of the city level.
 */
export const requiresAdjustLocation = (location: LandingSource): boolean =>
  !(isAddressComplete(location as AAALocation) && location.streetNumber !== '' && location.streetNumber != null)

/**
 * DRRWEB-3399 — turns a resolved breakdown location into a landing for the map. Landings exist only
 * while a landmark-zoom arm is on (see `landings$` in the breakdown location step). The strategy
 * itself applies only to an address the member typed into the search and selected; GPS, home,
 * query-param and pin-drop resolves carry control's empty chain and leave the zoom to the pre-ticket
 * pipeline — except a pin drop that follows a landing this feature widened, which the map re-lands
 * at the max zoom (`pinDrop`), unless the pin drop is on the place-type arm (`keepZoomOnPinDrop`).
 * `zoom` below is the fallback city zoom for adjust mode; on the `geocode` arm the landing's own
 * signal overrides it in `LandingDecider.landForAdjusting`. `restoredZoom` (DRRWEB-3716) is the zoom
 * the member left the map at, for the landing of a location restored on going back to the step.
 */
export const toMapLanding = (
  location: LandingSource,
  chain: ReferenceStrategyId[],
  restoredZoom: number | null = null
): MapLanding => {
  const eligible = location.locationType === LOCATION_TYPE.ADDRESS_INPUT
  const adjustRequired = eligible && requiresAdjustLocation(location)
  const pinDrop = location.locationType === LOCATION_TYPE.PIN_DROP
  return {
    key: toLocationKey(location),
    zoom: adjustRequired ? CITY_LEVEL_ZOOM : DEFAULT_MAX_MAP_ZOOM,
    adjustRequired,
    eligible,
    chain: eligible ? chain : [],
    pinDrop,
    // DRRWEB-3662 — the place-type arm holds the zoom on a drag; the decider reads this flag, not the
    // chain, which a pin drop does not carry (no strategy runs for it).
    keepZoomOnPinDrop: pinDrop && chain.includes('geocode'),
    completeAddress: Boolean(location.streetNumber && location.streetName),
    geocode: classifyPlaceTypeZoom(location.placeType),
    placeType: location.placeType ?? null,
    ...(restoredZoom != null ? { restoredZoom } : {}),
  }
}

export const hasResolvedCoordinates = (location: LandingSource | null | undefined): boolean =>
  Boolean(location?.latitude && location?.longitude)

/**
 * DRRWEB-3662 (B2) — the analytics row for a control landing. No strategy runs and the map does not
 * move (the pre-ticket zoom pipeline lands it at the max zoom), so the row says exactly that:
 * strategy and reference empty, zoom before equal to zoom after. It exists so the "adjusted" rate,
 * the time to confirmation and the interaction counters are comparable across arms and control per
 * place type — without it control has no landing row at all.
 */
export const toControlProbeOutcome = (location: LandingSource): ReferenceProbeOutcome => {
  const adjustRequired = requiresAdjustLocation(location)
  // Control's own pipeline lands an incomplete address at the city zoom (adjust mode), a complete
  // one at the max zoom — the row says where the map actually is.
  const zoom = adjustRequired ? CITY_LEVEL_ZOOM : DEFAULT_MAX_MAP_ZOOM
  return {
    reference: null,
    strategy: null,
    rendered: null,
    matchedLayerIds: [],
    kind: 'visible',
    nearbyWaitMs: 0,
    nearbyTimedOut: false,
    from: zoom,
    to: zoom,
    adjustRequired,
    ...(location.placeType ? { placeType: location.placeType } : {}),
  }
}

/**
 * DRRWEB-3399 — true when the landmark-zoom feature owns the zoom for this resolved location: an
 * arm is on and the location is a searched address (or a pin drop, which the map may re-land to
 * undo a widen). For everything else — and for every control member — the pre-ticket zoom
 * pipeline applies, unchanged.
 */
export const isArmedLanding = (
  location: Pick<AAALocation, 'locationType'> | null | undefined,
  chain: ReferenceStrategyId[]
): boolean =>
  chain.length > 0 &&
  (location?.locationType === LOCATION_TYPE.ADDRESS_INPUT ||
    location?.locationType === LOCATION_TYPE.PIN_DROP)
